/**
 * Live updates over server-sent events (ADR-0014).
 *
 * The stream carries invalidations, not records: an event says *something about
 * this project changed*, and the client refetches through the ordinary
 * endpoint. That keeps authorisation in one place — the API — rather than
 * duplicating it into a second delivery path.
 *
 * `EventSource` handles reconnection and replays from `Last-Event-ID` on its
 * own, so there is no retry logic here to get wrong.
 */
export interface ChangeEvent {
  event: string
  data: {
    subjectType?: string
    subjectId?: string
    actorId?: string | null
    at?: string
  }
}

export interface StreamHandle {
  close: () => void
}

export function streamProject(
  projectKey: string,
  onChange: (change: ChangeEvent) => void,
): StreamHandle {
  // Same origin as the API, which the dev server proxies — so the session
  // cookie is sent and no token is ever put in a query string.
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
  const source = new EventSource(`${base}/projects/${encodeURIComponent(projectKey)}/stream`, {
    withCredentials: true,
  })

  source.addEventListener('change', (event) => {
    try {
      onChange(JSON.parse((event as MessageEvent<string>).data) as ChangeEvent)
    } catch {
      // A malformed frame is not worth breaking the board over; the next one
      // will arrive, and a refetch is idempotent anyway.
    }
  })

  return { close: () => source.close() }
}
